import {createTheme, responsiveFontSizes} from "@mui/material/styles";

// Modern, professional palette tuned for legal/B2B SaaS.
// Primary matches the Jurybox logo: dark navy + sky blue accent.
const palette = {
    mode: "light",
    primary: {
        light: "#29A9E1",
        main: "#1B3C6E",
        dark: "#102848",
        contrastText: "#FFFFFF"
    },
    secondary: {
        light: "#64748B",
        main: "#334155",
        dark: "#1E293B",
        contrastText: "#FFFFFF"
    },
    text: {
        primary: "#0F172A",
        secondary: "#475569"
    },
    background: {
        default: "#FFFFFF",
        paper: "#FFFFFF"
    },
    divider: "#E3E8EF",
    grey: {
        50: "#F7F9FC",
        100: "#EEF2F7",
        200: "#E3E8EF",
        300: "#CDD5DF",
        400: "#9AA4B2",
        500: "#697586",
        600: "#4B5565",
        700: "#364152",
        800: "#202939",
        900: "#121926"
    }
};

const fontFamily = [
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif"
].join(",");

let theme = createTheme({
    palette,
    shape: {
        borderRadius: 12
    },
    typography: {
        fontFamily,
        h1: {
            fontWeight: 700,
            fontSize: "3.5rem",
            letterSpacing: "-0.025em",
            lineHeight: 1.1
        },
        h2: {
            fontWeight: 700,
            fontSize: "2.5rem",
            letterSpacing: "-0.02em",
            lineHeight: 1.15
        },
        h3: {
            fontWeight: 700,
            fontSize: "2rem",
            letterSpacing: "-0.015em",
            lineHeight: 1.2
        },
        h4: {
            fontWeight: 700,
            fontSize: "1.625rem",
            letterSpacing: "-0.01em",
            lineHeight: 1.25
        },
        h5: {
            fontWeight: 600,
            fontSize: "1.25rem",
            letterSpacing: "-0.005em",
            lineHeight: 1.35
        },
        h6: {
            fontWeight: 600,
            fontSize: "1.0625rem",
            lineHeight: 1.4
        },
        subtitle1: {
            fontSize: "1.125rem",
            lineHeight: 1.55,
            fontWeight: 400
        },
        body1: {
            fontSize: "1rem",
            lineHeight: 1.65
        },
        body2: {
            fontSize: "0.9375rem",
            lineHeight: 1.6
        },
        button: {
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: 0
        },
        overline: {
            fontWeight: 700,
            letterSpacing: "0.12em",
            fontSize: "0.75rem"
        }
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    scrollBehavior: "smooth"
                },
                body: {
                    backgroundColor: "#FFFFFF"
                },
                "::selection": {
                    backgroundColor: "rgba(41, 169, 225, 0.18)"
                }
            }
        },
        MuiAppBar: {
            defaultProps: {elevation: 0},
            styleOverrides: {
                root: {
                    backgroundColor: "rgba(255,255,255,0.85)",
                    backdropFilter: "saturate(180%) blur(10px)",
                    WebkitBackdropFilter: "saturate(180%) blur(10px)",
                    color: "#0F172A",
                    borderBottom: "1px solid #E3E8EF"
                }
            }
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true
            },
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    paddingInline: "1.1rem",
                    paddingBlock: "0.55rem",
                    transition: "transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease"
                },
                sizeLarge: {
                    paddingInline: "1.6rem",
                    paddingBlock: "0.8rem",
                    fontSize: "1rem"
                },
                containedPrimary: {
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(27, 60, 110, 0.08)",
                    "&:hover": {
                        boxShadow: "0 8px 24px rgba(27, 60, 110, 0.28)",
                        transform: "translateY(-1px)"
                    }
                },
                outlined: {
                    borderWidth: 1.5,
                    "&:hover": {
                        borderWidth: 1.5
                    }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                outlined: {
                    borderColor: "#E3E8EF"
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 999
                },
                outlined: {
                    borderWidth: 1.5
                }
            }
        },
        MuiAccordion: {
            defaultProps: {
                disableGutters: true,
                elevation: 0,
                square: false
            },
            styleOverrides: {
                root: {
                    border: "1px solid #E3E8EF",
                    borderRadius: 14,
                    overflow: "hidden",
                    marginBottom: 12,
                    "&:before": {display: "none"},
                    "&.Mui-expanded": {
                        borderColor: "#B8D9EE",
                        boxShadow: "0 12px 32px rgba(27, 60, 110, 0.08)"
                    }
                }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    paddingInline: "1.25rem",
                    paddingBlock: "0.25rem",
                    minHeight: 64,
                    "&.Mui-expanded": {
                        minHeight: 64
                    }
                },
                content: {
                    marginBlock: "1rem",
                    "&.Mui-expanded": {
                        marginBlock: "1rem"
                    }
                }
            }
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    paddingInline: "1.25rem",
                    paddingBottom: "1.25rem",
                    paddingTop: 0,
                    color: "#475569"
                }
            }
        },
        MuiLink: {
            defaultProps: {underline: "hover"},
            styleOverrides: {
                root: {
                    fontWeight: 500
                }
            }
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingLeft: "1.25rem",
                    paddingRight: "1.25rem"
                }
            }
        }
    }
});

theme = responsiveFontSizes(theme, {factor: 2.2});

export default theme;
